/* eslint-disable max-statements */
import { PartialAddress } from '@dreryk/address-common-api';
import { RepresentativeEntitlementStatementCreateCommand } from '@dreryk/egabinet-patient-frontend-api';
import { FirstName, LastName } from '@dreryk/person-name-common-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice, Range, Slot } from '@dreryk/watermelon';

import { createIdentityDocumentFromFormPart } from '../identityDocument/IdentityDocumentFormSection';
import { entitlementStatementsTranslationKeys } from '../translations';

import { RepresentativeEntitlementStatementCreateFormBean } from './create/form/RepresentativeEntitlementStatementCreateForm';

// eslint-disable-next-line max-lines-per-function
export const useCreateRepresentativeEntitlementStatement = (
   patientId: number,
   onDataChanged: VoidFunction
): UseCqrsCommandResult<number, RepresentativeEntitlementStatementCreateFormBean> => {
   return useCqrsCommand<number, RepresentativeEntitlementStatementCreateFormBean>(
      (parameters) => {
         const command = new RepresentativeEntitlementStatementCreateCommand();
         command.effectiveDate = parameters.effectiveDate!;
         command.legalBasis = parameters.legalBasis!.value;
         command.patientAddress = PartialAddress.builder()
            .withPostcode(parameters.postcode)
            .withTown(parameters.town)
            .withStreet(parameters.street)
            .withBuilding(parameters.house)
            .withFlat(parameters.flat)
            .build();
         command.patientId = patientId;
         command.patientIdentityDocument = createIdentityDocumentFromFormPart(parameters.identityDocument)!;
         command.submissionDate = parameters.submissionDate!;

         if (parameters.grantingDateFieldEnabled) {
            command.providingDateRange = Slot.of(
               Range.closed(parameters.grantingDate.lowerEndpoint(), parameters.grantingDate.upperEndpoint())
            );
         }

         command.representative = {
            address: PartialAddress.builder()
               .withPostcode(parameters.representativePostcode)
               .withTown(parameters.representativeTown)
               .withStreet(parameters.representativeStreet)
               .withBuilding(parameters.representativeHouse)
               .withFlat(parameters.representativeFlat)
               .build(),
            firstName: FirstName.of(parameters.firstName),
            identityDocument: createIdentityDocumentFromFormPart(parameters.representativeIdentityDocument)!,
            lastName: LastName.of(parameters.lastName),
            pesel: Choice.withUseDefaultIfNull(parameters.pesel ?? null),
            relationshipType: parameters.guardianRelation,
         };

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(entitlementStatementsTranslationKeys.createModal.documentAdded, {
               autoHideDelayInMs: 10000,
            });
            onDataChanged();
         },
      }
   );
};
